import { useState, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════════════════
// LOCATION PANEL
// ═══════════════════════════════════════════════════════════════════════════
// Geography. Tabs over the fields Proxy actually stores: Market on every
// record, Submarket on sourcing properties (and comps, as micro_market), City
// on properties and comps.
//
// County is deliberately absent. Only `deals.counties` exists — sourcing
// properties have no county column — so a County tab would hide every
// property the moment you used it.
//
// CoStar uses two lists with arrow buttons between them. Click-to-move is
// fewer actions for the same result, so available values add on click and
// selected ones remove on click.

const TABS = [
  { id: "market",    label: "Market" },
  { id: "submarket", label: "Submarket" },
  { id: "city",      label: "City" },
];

export default function MapLocationPanel({ S, options, selected, onChange, onClearAll }) {
  const [tabId, setTabId] = useState("market");
  const [q, setQ] = useState("");

  const tab = TABS.find(t => t.id === tabId) || TABS[0];
  const opt = options[tabId] || { values: [], missing: 0 };
  // Memoized because the `|| new Set()` fallback would otherwise be a fresh
  // object every render and invalidate the memos below on each keystroke.
  const sel = useMemo(() => selected[tabId] || new Set(), [selected, tabId]);

  const available = useMemo(() => {
    const qLc = q.trim().toLowerCase();
    return opt.values.filter(v => !sel.has(v) && (!qLc || v.toLowerCase().includes(qLc)));
  }, [opt.values, sel, q]);

  const selectedList = useMemo(() => [...sel].sort((a, b) => a.localeCompare(b)), [sel]);
  const totalSelected = TABS.reduce((n, t) => n + ((selected[t.id] || new Set()).size), 0);

  function add(v) {
    const next = new Set(sel);
    next.add(v);
    onChange(tabId, next);
  }
  function remove(v) {
    const next = new Set(sel);
    next.delete(v);
    onChange(tabId, next);
  }

  const listBox = {
    height: 168, overflowY: "auto", background: S.card,
    border: `1px solid ${S.border}`, borderRadius: 7, padding: 4,
  };
  const rowBtn = {
    display: "block", width: "100%", textAlign: "left", background: "transparent",
    border: "none", borderRadius: 5, color: S.text, padding: "5px 7px",
    fontSize: 11, fontWeight: 600, fontFamily: S.font, cursor: "pointer",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
        {TABS.map(t => {
          const active = t.id === tabId;
          const n = (selected[t.id] || new Set()).size;
          return (
            <button key={t.id} onClick={() => { setTabId(t.id); setQ(""); }}
              style={{
                flex: 1, background: active ? S.red : "transparent",
                border: `1px solid ${active ? S.red : S.border}`, borderRadius: 6,
                color: active ? "#ffffff" : S.text, padding: "5px 4px",
                fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer",
              }}>
              {t.label}{n > 0 ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>

      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder={`Search ${tab.label.toLowerCase()}s…`}
        style={{ width: "100%", boxSizing: "border-box", background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, padding: "7px 9px", color: S.text, fontSize: 11, fontFamily: S.font, outline: "none", marginBottom: 8 }} />

      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.muted, marginBottom: 4 }}>
        Available · {available.length}
      </div>
      <div style={{ ...listBox, marginBottom: 9 }}>
        {available.length === 0 ? (
          <div style={{ padding: "10px 7px", fontSize: 10.5, color: S.muted }}>
            {opt.values.length === 0
              ? `No ${tab.label.toLowerCase()} values on the pins currently loaded.`
              : "Everything matching is already selected."}
          </div>
        ) : available.map(v => (
          <button key={v} onClick={() => add(v)} style={rowBtn}
            onMouseEnter={e => { e.currentTarget.style.background = S.card2 || S.border; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            + {v}
          </button>
        ))}
      </div>

      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.muted, marginBottom: 4 }}>
        Selected · {selectedList.length}
      </div>
      <div style={listBox}>
        {selectedList.length === 0 ? (
          <div style={{ padding: "10px 7px", fontSize: 10.5, color: S.muted }}>
            Nothing selected — all {tab.label.toLowerCase()}s shown.
          </div>
        ) : selectedList.map(v => (
          <button key={v} onClick={() => remove(v)} style={{ ...rowBtn, color: S.red }}
            onMouseEnter={e => { e.currentTarget.style.background = `${S.red}14`; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            ✕ {v}
          </button>
        ))}
      </div>

      {/* Honest warning: selecting a field some pins don't carry hides them. */}
      {sel.size > 0 && opt.missing > 0 && (
        <div style={{ marginTop: 8, fontSize: 9.5, color: S.muted, lineHeight: 1.45 }}>
          {opt.missing.toLocaleString()} loaded pin{opt.missing === 1 ? " has" : "s have"} no {tab.label.toLowerCase()} recorded
          and {opt.missing === 1 ? "is" : "are"} hidden while this filter is on.
        </div>
      )}

      {totalSelected > 0 && (
        <button onClick={onClearAll}
          style={{ width: "100%", marginTop: 9, background: "transparent", border: `1px solid ${S.border}`, borderRadius: 7, color: S.muted, padding: "6px 9px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
          Clear all location filters ({totalSelected})
        </button>
      )}
    </>
  );
}
